<template>
  <LoadingCard :loading="loading" class="overflow-visible">
    <!-- ─── Header ──────────────────────────────────────────── -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center gap-3">
        <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
          <svg class="h-4.5 w-4.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.25 4.533A9.707 9.707 0 006 3a9.735 9.735 0 00-3.25.555.75.75 0 00-.5.707v14.25a.75.75 0 001 .707A8.237 8.237 0 016 18.75c1.995 0 3.823.707 5.25 1.886V4.533zM12.75 20.636A8.214 8.214 0 0118 18.75c.966 0 1.89.166 2.75.47a.75.75 0 001-.708V4.262a.75.75 0 00-.5-.707A9.735 9.735 0 0018 3a9.707 9.707 0 00-5.25 1.533v16.103z" />
          </svg>
        </div>
        <div>
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">موسسه های در انتظار تأیید</h3>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">تلفن تأیید شده · اطلاعات یا مدارک در انتظار بررسی</p>
        </div>
      </div>
      <div class="flex items-center gap-1.5">
        <span
          v-if="institutes.length > 0"
          class="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-primary-50 dark:bg-primary-900/30 px-2 text-xs font-semibold text-primary-700 dark:text-primary-400"
        >
          {{ institutes.length }}
        </span>
        <button
          @click="fetch"
          :disabled="loading"
          class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:text-primary-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 dark:hover:text-primary-400 transition-colors"
          title="بارگذاری مجدد"
        >
          <svg :class="['h-4 w-4', loading ? 'animate-spin' : '']" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
    </div>

    <!-- ─── Empty State ─────────────────────────────────────── -->
    <div v-if="!loading && institutes.length === 0" class="flex flex-col items-center justify-center py-12 px-6 text-center">
      <div class="flex h-10 w-10 items-center justify-center rounded-full bg-green-50 dark:bg-green-900/20 mb-3">
        <svg class="h-5 w-5 text-green-600 dark:text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clip-rule="evenodd" />
        </svg>
      </div>
      <p class="text-sm font-medium text-gray-600 dark:text-gray-300">هیچ موسسه در انتظار تأیید نیست</p>
      <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">همه موسسه ها فعال هستند</p>
    </div>

    <!-- ─── Institute List ──────────────────────────────────── -->
    <div v-else-if="institutes.length > 0" class="divide-y divide-gray-100 dark:divide-gray-700/60">
      <div
        v-for="institute in institutes"
        :key="institute.id"
        @click="openModal(institute)"
        class="group flex items-center gap-3 px-4 sm:px-6 py-3 sm:py-3.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors last:rounded-b-xl"
      >
        <!-- Avatar -->
        <div class="flex h-8 w-8 sm:h-9 sm:w-9 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-300">
          {{ initial(institute.name) }}
        </div>

        <!-- Content: 2-line on mobile, single row on desktop -->
        <div class="flex flex-1 min-w-0 flex-col sm:flex-row sm:items-center sm:gap-4">
          <!-- Name + code -->
          <div class="min-w-0 sm:w-40 sm:flex-shrink-0">
            <p class="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{{ institute.name }}</p>
            <p class="text-xs font-mono text-gray-400 dark:text-gray-500 mt-0.5">{{ institute.code }}</p>
          </div>

          <!-- Phone (desktop only) -->
          <div class="hidden sm:block w-28 flex-shrink-0 text-xs text-gray-500 dark:text-gray-400" dir="ltr">{{ institute.phone }}</div>

          <!-- Status badges -->
          <div class="flex flex-wrap items-center gap-1 mt-1.5 sm:mt-0 sm:flex-1 sm:gap-1.5">
            <StatusBadge tone="gray">تلفن تأیید شده</StatusBadge>
            <StatusBadge v-if="isInfoActionable(institute)" tone="primary">اطلاعات در انتظار</StatusBadge>
            <StatusBadge v-else-if="isInfoApproved(institute)" tone="green">اطلاعات تأیید شد</StatusBadge>
            <StatusBadge v-else-if="isInfoRejected(institute)" tone="red">اطلاعات رد شد</StatusBadge>
            <StatusBadge v-if="isDocsActionable(institute)" tone="primary">مدارک در انتظار</StatusBadge>
            <StatusBadge v-else-if="isDocsApproved(institute)" tone="green">مدارک تأیید شد</StatusBadge>
            <StatusBadge v-else-if="isDocsRejected(institute)" tone="red">مدارک رد شد</StatusBadge>
          </div>
        </div>

        <!-- Chevron -->
        <svg class="h-4 w-4 flex-shrink-0 text-gray-300 group-hover:text-primary-500 transition-colors" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>

    <!-- ─── Modal ───────────────────────────────────────────── -->
    <Modal
      v-if="selected"
      :show="!!selected"
      size="4xl"
      @close-via-escape="closeModal"
    >
      <div class="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-lg">

        <!-- Modal Header -->
        <div class="flex items-center justify-between px-8 py-5 border-b border-gray-100 dark:border-gray-700">
          <div class="flex items-center gap-3">
            <div class="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
              <svg class="h-4.5 w-4.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path fill-rule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clip-rule="evenodd" />
              </svg>
            </div>
            <div>
              <h2 class="text-base font-bold text-gray-900 dark:text-gray-100">{{ selected.name }}</h2>
              <p class="text-xs font-mono text-gray-400 dark:text-gray-500 mt-0.5" dir="ltr">{{ selected.code }} · {{ selected.phone }}</p>
            </div>
          </div>
          <button
            @click="closeModal"
            class="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <svg class="h-4.5 w-4.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Tabs -->
        <div class="flex gap-1 border-b border-gray-100 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/50 px-8">
          <button
            @click="activeTab = 'info'"
            :class="[
              'relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors focus:outline-none',
              activeTab === 'info'
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            ]"
          >
            اطلاعات اصلی
            <TabStatusDot :status="infoStatus" />
            <span v-if="activeTab === 'info'" class="absolute bottom-0 left-0 right-0 h-0.5 rounded-t bg-primary-500"></span>
          </button>
          <button
            @click="activeTab = 'docs'"
            :class="[
              'relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors focus:outline-none',
              activeTab === 'docs'
                ? 'text-primary-600 dark:text-primary-400'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            ]"
          >
            مدارک هویتی
            <TabStatusDot :status="docsStatus" />
            <span v-if="activeTab === 'docs'" class="absolute bottom-0 left-0 right-0 h-0.5 rounded-t bg-primary-500"></span>
          </button>
        </div>

        <!-- Tab Content -->
        <div class="max-h-[70vh] overflow-y-auto">

          <!-- ── Info Tab ────────────────────────────────────── -->
          <div v-if="activeTab === 'info'" class="p-8">
            <div v-if="selected.info">

              <!-- Resolved status banner -->
              <ResolvedBanner
                v-if="infoStatus === 'approved'"
                tone="green"
                title="اطلاعات اصلی تأیید شده است"
                :date="selected.info.reviewed_at"
              />
              <ResolvedBanner
                v-else-if="infoStatus === 'rejected'"
                tone="red"
                title="اطلاعات اصلی رد شده است"
                :reason="selected.info.rejection_reason"
                :date="selected.info.reviewed_at"
              />

              <!-- Fields table -->
              <div class="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
                <div class="bg-gray-50 dark:bg-gray-700/50 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                  <p class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">فیلدهای ثبت‌شده</p>
                </div>
                <div class="divide-y divide-gray-100 dark:divide-gray-700">
                  <template v-for="field in infoFields" :key="field.key">
                    <!-- Image field -->
                    <div v-if="field.type === 'image'" class="flex items-start gap-4 px-4 py-3">
                      <span class="w-36 flex-shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400 pt-1">{{ field.label }}</span>
                      <div class="flex-1">
                        <a v-if="field.value" :href="field.value" target="_blank" class="inline-block">
                          <img :src="field.value" class="h-20 w-auto rounded-md border border-gray-200 dark:border-gray-600 object-cover" />
                        </a>
                        <span v-else class="text-xs text-gray-400 dark:text-gray-500 italic">بدون تصویر</span>
                      </div>
                    </div>
                    <!-- Text field -->
                    <div v-else class="flex items-start gap-4 px-4 py-2.5">
                      <span class="w-36 flex-shrink-0 text-xs font-medium text-gray-500 dark:text-gray-400 pt-0.5">{{ field.label }}</span>
                      <span class="flex-1 text-sm text-gray-800 dark:text-gray-200 break-words">{{ field.value || '—' }}</span>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Action buttons / reject form -->
              <template v-if="infoStatus === 'pending' || infoStatus === 'reviewing'">
                <div v-if="!showRejectInfo" class="flex items-center gap-3">
                  <button
                    @click="handleApproveInfo"
                    :disabled="actionLoading"
                    class="inline-flex items-center gap-2 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-60 px-4 py-2 text-sm font-semibold text-white transition-colors"
                  >
                    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 011.04-.208z" clip-rule="evenodd" /></svg>
                    تأیید اطلاعات
                  </button>
                  <button
                    @click="showRejectInfo = true"
                    :disabled="actionLoading"
                    class="inline-flex items-center gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-white dark:bg-transparent hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-60 px-4 py-2 text-sm font-semibold text-red-600 dark:text-red-400 transition-colors"
                  >
                    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clip-rule="evenodd" /></svg>
                    رد اطلاعات
                  </button>
                </div>

                <!-- Reject form -->
                <div v-else class="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
                  <p class="text-sm font-semibold text-red-700 dark:text-red-400 mb-3">دلیل رد اطلاعات را بنویسید</p>
                  <textarea
                    v-model="rejectInfoReason"
                    rows="3"
                    placeholder="دلیل رد اطلاعات موسسه..."
                    class="w-full rounded-md border border-red-200 dark:border-red-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                  ></textarea>
                  <div class="flex items-center gap-3 mt-3">
                    <button
                      @click="handleRejectInfo"
                      :disabled="actionLoading || !rejectInfoReason.trim()"
                      class="inline-flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 px-4 py-2 text-sm font-semibold text-white transition-colors"
                    >
                      <svg v-if="actionLoading" class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                      تأیید رد
                    </button>
                    <button
                      @click="showRejectInfo = false; rejectInfoReason = ''"
                      :disabled="actionLoading"
                      class="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    >
                      انصراف
                    </button>
                  </div>
                </div>
              </template>
            </div>
            <div v-else class="flex flex-col items-center justify-center py-12 text-gray-400">
              <svg class="h-10 w-10 mb-3" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 01-.988-1.129c1.454-1.272 3.776-1.272 5.23 0 1.513 1.324 1.513 3.518 0 4.842a3.75 3.75 0 01-.837.552c-.676.328-1.028.774-1.028 1.152v.75a.75.75 0 01-1.5 0v-.75c0-1.279 1.06-2.107 1.875-2.502.182-.088.351-.199.503-.331.83-.727.83-1.857 0-2.584zM12 18a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd" /></svg>
              <p class="text-sm">اطلاعات اصلی موجود نیست</p>
            </div>
          </div>

          <!-- ── Docs Tab ─────────────────────────────────────── -->
          <div v-else-if="activeTab === 'docs'" class="p-8">
            <div v-if="selected.docs">

              <!-- Resolved status banner -->
              <ResolvedBanner
                v-if="docsStatus === 'approved'"
                tone="green"
                title="مدارک تأیید شده است"
                :date="selected.docs.reviewed_at"
              />
              <ResolvedBanner
                v-else-if="docsStatus === 'rejected'"
                tone="red"
                title="مدارک رد شده است"
                :reason="selected.docs.reject_reason"
                :date="selected.docs.reviewed_at"
              />

              <!-- Document images -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div class="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div class="bg-gray-50 dark:bg-gray-700/50 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                    <p class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">کارت ملی</p>
                  </div>
                  <div class="p-4 flex items-center justify-center min-h-[160px] bg-gray-50 dark:bg-gray-800/30">
                    <a v-if="selected.docs.national_id_card_url" :href="selected.docs.national_id_card_url" target="_blank">
                      <img :src="selected.docs.national_id_card_url" class="max-h-48 w-auto rounded-md object-contain cursor-zoom-in" />
                    </a>
                    <div v-else class="flex flex-col items-center text-gray-400 dark:text-gray-500">
                      <svg class="h-10 w-10 mb-2" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z" clip-rule="evenodd" /></svg>
                      <p class="text-xs">بدون تصویر</p>
                    </div>
                  </div>
                </div>

                <div class="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div class="bg-gray-50 dark:bg-gray-700/50 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                    <p class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">اسکن مجوز</p>
                  </div>
                  <div class="p-4 flex items-center justify-center min-h-[160px] bg-gray-50 dark:bg-gray-800/30">
                    <a v-if="selected.docs.license_scan_url" :href="selected.docs.license_scan_url" target="_blank">
                      <img :src="selected.docs.license_scan_url" class="max-h-48 w-auto rounded-md object-contain cursor-zoom-in" />
                    </a>
                    <div v-else class="flex flex-col items-center text-gray-400 dark:text-gray-500">
                      <svg class="h-10 w-10 mb-2" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z" clip-rule="evenodd" /></svg>
                      <p class="text-xs">بدون تصویر</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action buttons / reject form -->
              <template v-if="docsStatus === 'pending'">
                <div v-if="!showRejectDocs" class="flex items-center gap-3">
                  <button
                    @click="handleApproveDocs"
                    :disabled="actionLoading"
                    class="inline-flex items-center gap-2 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-60 px-4 py-2 text-sm font-semibold text-white transition-colors"
                  >
                    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 011.04-.208z" clip-rule="evenodd" /></svg>
                    تأیید مدارک
                  </button>
                  <button
                    @click="showRejectDocs = true"
                    :disabled="actionLoading"
                    class="inline-flex items-center gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-white dark:bg-transparent hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-60 px-4 py-2 text-sm font-semibold text-red-600 dark:text-red-400 transition-colors"
                  >
                    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clip-rule="evenodd" /></svg>
                    رد مدارک
                  </button>
                </div>

                <!-- Reject form -->
                <div v-else class="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
                  <p class="text-sm font-semibold text-red-700 dark:text-red-400 mb-3">دلیل رد مدارک را بنویسید</p>
                  <textarea
                    v-model="rejectDocsReason"
                    rows="3"
                    placeholder="دلیل رد مدارک موسسه..."
                    class="w-full rounded-md border border-red-200 dark:border-red-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                  ></textarea>
                  <div class="flex items-center gap-3 mt-3">
                    <button
                      @click="handleRejectDocs"
                      :disabled="actionLoading || !rejectDocsReason.trim()"
                      class="inline-flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 px-4 py-2 text-sm font-semibold text-white transition-colors"
                    >
                      <svg v-if="actionLoading" class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                      تأیید رد
                    </button>
                    <button
                      @click="showRejectDocs = false; rejectDocsReason = ''"
                      :disabled="actionLoading"
                      class="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    >
                      انصراف
                    </button>
                  </div>
                </div>
              </template>

              <!-- Allow re-review after a rejection -->
              <div v-else-if="docsStatus === 'rejected'" class="flex items-center gap-3">
                <button
                  @click="handleApproveDocs"
                  :disabled="actionLoading"
                  class="inline-flex items-center gap-2 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-60 px-4 py-2 text-sm font-semibold text-white transition-colors"
                >
                  <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 011.04-.208z" clip-rule="evenodd" /></svg>
                  تأیید مدارک
                </button>
              </div>
            </div>
            <div v-else class="flex flex-col items-center justify-center py-12 text-gray-400">
              <svg class="h-10 w-10 mb-3" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 01-.988-1.129c1.454-1.272 3.776-1.272 5.23 0 1.513 1.324 1.513 3.518 0 4.842a3.75 3.75 0 01-.837.552c-.676.328-1.028.774-1.028 1.152v.75a.75.75 0 01-1.5 0v-.75c0-1.279 1.06-2.107 1.875-2.502.182-.088.351-.199.503-.331.83-.727.83-1.857 0-2.584zM12 18a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd" /></svg>
              <p class="text-sm">مدارک موجود نیست</p>
            </div>
          </div>

        </div>

        <!-- Modal Footer -->
        <div class="flex items-center justify-between px-8 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700">
          <div class="flex items-center gap-2">
            <span v-if="actionSuccess" class="inline-flex items-center gap-1.5 text-sm font-medium text-green-600 dark:text-green-400">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clip-rule="evenodd" /></svg>
              {{ actionSuccess }}
            </span>
            <span v-if="actionError" class="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 dark:text-red-400">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path fill-rule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clip-rule="evenodd" /></svg>
              {{ actionError }}
            </span>
          </div>
          <button
            @click="closeModal"
            class="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            بستن
          </button>
        </div>

      </div>
    </Modal>

    <Modal
      v-if="confirmDialog.show"
      :show="confirmDialog.show"
      size="sm"
      @close-via-escape="closeConfirmDialog"
    >
      <div class="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-xl">
        <div class="px-6 pt-8 pb-6 text-center">
          <div :class="[
            'mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full',
            confirmDialog.tone === 'danger' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'
          ]">
            <svg v-if="confirmDialog.tone === 'danger'" :class="['h-8 w-8', 'text-red-600 dark:text-red-400']" viewBox="0 0 24 24" fill="currentColor">
              <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd" />
            </svg>
            <svg v-else :class="['h-8 w-8', 'text-green-600 dark:text-green-400']" viewBox="0 0 24 24" fill="currentColor">
              <path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clip-rule="evenodd" />
            </svg>
          </div>
          <h3 class="text-base font-bold text-gray-900 dark:text-gray-100 mb-2">{{ confirmDialog.title }}</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{{ confirmDialog.message }}</p>
        </div>
        <div class="flex items-center gap-3 px-6 pb-6">
          <button
            @click="closeConfirmDialog"
            class="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            انصراف
          </button>
          <button
            @click="runConfirmedAction"
            :class="[
              'flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-colors',
              confirmDialog.tone === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
            ]"
          >
            تأیید
          </button>
        </div>
      </div>
    </Modal>

  </LoadingCard>
</template>

<script>
const StatusBadge = {
  props: { tone: String },
  computed: {
    classes() {
      const tones = {
        gray: { wrap: 'bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300', dot: 'bg-gray-400' },
        primary: { wrap: 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400', dot: 'bg-primary-500' },
        green: { wrap: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400', dot: 'bg-green-500' },
        red: { wrap: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400', dot: 'bg-red-500' },
      }
      return tones[this.tone] ?? tones.gray
    },
  },
  render() {
    return window.Vue.h('span', {
      class: `inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-medium ${this.classes.wrap}`,
    }, [
      window.Vue.h('span', { class: `h-1.5 w-1.5 flex-shrink-0 rounded-full ${this.classes.dot}` }),
      this.$slots.default ? this.$slots.default() : '',
    ])
  },
}

const TabStatusDot = {
  props: { status: String },
  render() {
    if (!this.status || this.status === 'pending' || this.status === 'reviewing') return null
    const colors = {
      approved: 'bg-green-500',
      rejected: 'bg-red-500',
    }
    return window.Vue.h('span', {
      class: `inline-block h-1.5 w-1.5 rounded-full ${colors[this.status] ?? 'bg-gray-400'}`,
    })
  },
}

const ResolvedBanner = {
  props: { tone: String, title: String, reason: String, date: String },
  render() {
    const tones = {
      green: {
        wrap: 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20',
        title: 'text-green-700 dark:text-green-400',
        text: 'text-green-600 dark:text-green-300',
      },
      red: {
        wrap: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20',
        title: 'text-red-700 dark:text-red-400',
        text: 'text-red-600 dark:text-red-300',
      },
    }
    const t = tones[this.tone] ?? tones.green
    const children = [
      window.Vue.h('p', { class: `text-sm font-semibold ${t.title} mb-1` }, this.title),
    ]
    if (this.reason) {
      children.push(window.Vue.h('p', { class: `text-sm ${t.text}` }, this.reason))
    }
    if (this.date) {
      children.push(window.Vue.h('p', { class: `text-xs ${t.text} opacity-70 mt-1.5` }, this.date))
    }
    return window.Vue.h('div', { class: `mb-6 rounded-lg border p-4 ${t.wrap}` }, children)
  },
}

export default {
  name: 'PendingInstituteActivationCard',

  components: { StatusBadge, TabStatusDot, ResolvedBanner },

  props: {
    card: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    loading: true,
    institutes: [],
    selected: null,
    activeTab: 'info',
    showRejectInfo: false,
    rejectInfoReason: '',
    showRejectDocs: false,
    rejectDocsReason: '',
    actionLoading: false,
    actionSuccess: '',
    actionError: '',
    confirmDialog: {
      show: false,
      title: '',
      message: '',
      tone: 'success',
      action: null,
    },
  }),

  computed: {
    infoFields() {
      return this.selected?.info?.fields ?? []
    },

    infoStatus() {
      return this.selected?.info?.status ?? null
    },

    docsStatus() {
      return this.selected?.docs?.status ?? null
    },

    endpoint() {
      return this.card?.apiEndpoint ?? '/dashboard-api/activation/institutes'
    },
  },

  created() {
    this.fetch()
  },

  methods: {
    initial(name) {
      return (name ?? '').trim().charAt(0) || '?'
    },
    isInfoActionable(institute) {
      return ['pending', 'reviewing'].includes(institute.info?.status)
    },
    isInfoApproved(institute) {
      return institute.info?.status === 'approved'
    },
    isInfoRejected(institute) {
      return institute.info?.status === 'rejected'
    },
    isDocsActionable(institute) {
      return institute.docs?.status === 'pending'
    },
    isDocsApproved(institute) {
      return institute.docs?.status === 'approved'
    },
    isDocsRejected(institute) {
      return institute.docs?.status === 'rejected'
    },

    async fetch() {
      this.loading = true
      try {
        const { data } = await Nova.request().get(this.endpoint)
        this.institutes = data.institutes ?? []
      } catch {
        this.institutes = []
      } finally {
        this.loading = false
      }
    },

    openModal(institute) {
      this.selected = institute
      this.activeTab = 'info'
      this.resetForms()
    },

    closeModal() {
      this.selected = null
      this.resetForms()
    },

    resetForms() {
      this.showRejectInfo = false
      this.rejectInfoReason = ''
      this.showRejectDocs = false
      this.rejectDocsReason = ''
      this.actionSuccess = ''
      this.actionError = ''
    },

    async handleApproveInfo() {
      this.openConfirmDialog({
        title: 'تایید اطلاعات اصلی',
        message: 'آیا از تایید اطلاعات اصلی این موسسه مطمئن هستید؟',
        tone: 'success',
        action: () => this.doAction(`${this.endpoint}/${this.selected.id}/approve-info`, {}, 'اطلاعات اصلی تأیید شد'),
      })
    },

    async handleRejectInfo() {
      this.openConfirmDialog({
        title: 'رد اطلاعات اصلی',
        message: 'آیا از رد اطلاعات اصلی این موسسه مطمئن هستید؟',
        tone: 'danger',
        action: async () => {
          const ok = await this.doAction(
            `${this.endpoint}/${this.selected.id}/reject-info`,
            { reason: this.rejectInfoReason },
            'اطلاعات اصلی رد شد'
          )
          if (ok) this.showRejectInfo = false
        },
      })
    },

    async handleApproveDocs() {
      this.openConfirmDialog({
        title: 'تایید مدارک هویتی',
        message: 'آیا از تایید مدارک این موسسه مطمئن هستید؟',
        tone: 'success',
        action: () => this.doAction(`${this.endpoint}/${this.selected.id}/approve-docs`, {}, 'مدارک تأیید شد'),
      })
    },

    async handleRejectDocs() {
      this.openConfirmDialog({
        title: 'رد مدارک هویتی',
        message: 'آیا از رد مدارک این موسسه مطمئن هستید؟',
        tone: 'danger',
        action: async () => {
          const ok = await this.doAction(
            `${this.endpoint}/${this.selected.id}/reject-docs`,
            { reason: this.rejectDocsReason },
            'مدارک رد شد'
          )
          if (ok) this.showRejectDocs = false
        },
      })
    },

    openConfirmDialog({ title, message, tone = 'success', action }) {
      this.confirmDialog = { show: true, title, message, tone, action }
    },

    closeConfirmDialog() {
      this.confirmDialog = {
        show: false,
        title: '',
        message: '',
        tone: 'success',
        action: null,
      }
    },

    async runConfirmedAction() {
      const action = this.confirmDialog.action
      this.closeConfirmDialog()
      if (typeof action === 'function') {
        await action()
      }
    },

    async doAction(url, payload, successMsg) {
      this.actionLoading = true
      this.actionSuccess = ''
      this.actionError = ''
      const selectedId = this.selected?.id
      try {
        await Nova.request().post(url, payload)
        this.actionSuccess = successMsg
        await this.fetch()

        const stillPending = this.institutes.find(i => i.id === selectedId)
        if (stillPending) {
          this.selected = stillPending
        } else {
          setTimeout(() => {
            if (this.selected?.id === selectedId) this.closeModal()
          }, 1200)
        }
        return true
      } catch (err) {
        this.actionError = err?.response?.data?.message ?? 'خطایی رخ داد'
        return false
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>
